import { ListItem, makeStyles, Switch } from '@rneui/themed'
import { t } from 'app/i18l'
import React, { useEffect, useState } from 'react'
import { FlatList, Platform, View } from 'react-native'
import { OnBooleanSettingChanged, Setting } from 'settings/types'
import { SettingRow } from './row'

interface Props {
  settings: Setting[] | undefined
  onPressSetting(setting: Setting): void
  onBooleanSettingChanged: OnBooleanSettingChanged
  calendarSyncSettingValue?: boolean
  setCalendarSyncSettingValue?: React.Dispatch<
    React.SetStateAction<boolean | undefined>
  >
}

export function SettingsView({
  settings,
  onPressSetting,
  onBooleanSettingChanged,
  calendarSyncSettingValue,
  setCalendarSyncSettingValue,
}: Props) {
  const styles = useStyles()
  return (
    <View style={styles.container}>
      <FlatList
        style={styles.list}
        data={settings}
        keyExtractor={item => String(item.id)}
        renderItem={({ item }) =>
          isToggledOff(item.toggledBy, settings) ? null : (
            <View>
              <SettingRow
                setting={item}
                onPress={onPressSetting}
                onBooleanSettingChanged={onBooleanSettingChanged}
              />
              <DescriptionRow description={item.description} />
            </View>
          )
        }
        ListFooterComponent={
          calendarSyncSettingValue !== null &&
          Platform.OS !== 'web' &&
          setCalendarSyncSettingValue ? (
            <View style={styles.calendarSyncContainer}>
              <CalendarSyncSettingRowRow
                settingValue={calendarSyncSettingValue}
                setSettingValue={setCalendarSyncSettingValue}
              />
            </View>
          ) : null
        }
      />
    </View>
  )
}

interface DescriptionRowProps {
  description?: string
}

function DescriptionRow({ description }: DescriptionRowProps) {
  const styles = useStyles()
  return (
    <ListItem containerStyle={styles.descriptionContainer}>
      <ListItem.Content>
        <ListItem.Subtitle style={styles.descriptionText}>
          {description}
        </ListItem.Subtitle>
      </ListItem.Content>
    </ListItem>
  )
}

interface CalendarSyncSettingRowProps {
  settingValue: boolean | undefined
  setSettingValue: React.Dispatch<React.SetStateAction<boolean | undefined>>
}

function CalendarSyncSettingRowRow({
  settingValue,
  setSettingValue,
}: CalendarSyncSettingRowProps) {
  const [value, setValue] = useState<boolean | undefined>()

  useEffect(() => {
    setValue(settingValue)
  }, [settingValue])

  async function onValueChange() {
    const newValue = !value
    setValue(newValue)
    setSettingValue(newValue)
  }
  return (
    <ListItem>
      <ListItem.Content>
        <ListItem.Title>{t('syncToCalendar')}</ListItem.Title>
      </ListItem.Content>
      <ListItem.Content right>
        <Switch
          style={{ alignSelf: 'flex-end' }}
          value={value}
          onValueChange={onValueChange}
        />
      </ListItem.Content>
    </ListItem>
  )
}

function isToggledOff(togglingId?: string, settings?: Setting[]) {
  if (togglingId) {
    const togglingSetting = settings?.find(
      (setting: Setting) => setting.id === togglingId
    )
    return togglingSetting?.type === 'boolean' && !togglingSetting?.on
  } else return false
}

const useStyles = makeStyles(theme => ({
  container: {
    marginHorizontal: 0,
    paddingHorizontal: 0,
  },
  list: {
    paddingVertical: 10,
  },
  descriptionContainer: {
    backgroundColor: 'transparent',
  },
  descriptionText: {
    color: theme.colors?.grey1,
  },
  calendarSyncContainer: {
    marginTop: 0,
    marginBottom: 10,
  },
}))
